import { useState, useEffect } from 'react';
import { 
  Dialog, 
  DialogTitle, 
  DialogContent, 
  DialogActions, 
  TextField, 
  Button, 
  IconButton,
  Box,
  Typography,
  alpha,
  useMediaQuery,
  useTheme,
  Drawer,  // Add Drawer for mobile
  AppBar,   // Add AppBar for mobile header
  Toolbar   // Add Toolbar for mobile header
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';

interface NotesModalProps {
  isOpen: boolean;
  onClose: () => void;
  notes: string;
  onSave: (notes: string) => void;
  taskSummary: string;
}

const NotesModal = ({ isOpen, onClose, notes, onSave, taskSummary }: NotesModalProps) => {
  const [editedNotes, setEditedNotes] = useState(notes);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'), {
    defaultMatches: false,
    noSsr: true
  });
  
  // Log when props change to debug
  useEffect(() => {
    console.log('NotesModal isOpen:', isOpen, 'isMobile:', isMobile);
  }, [isOpen, isMobile]);
  
  useEffect(() => {
    setEditedNotes(notes);
  }, [notes, isOpen]);
  
  const handleSave = () => {
    onSave(editedNotes);
    onClose();
  };

  // For mobile, use a Drawer instead of Dialog
  if (isMobile) {
    return (
      <Drawer
        anchor="bottom"
        open={isOpen}
        onClose={onClose}
        sx={{
          '& .MuiDrawer-paper': {
            height: '100%',
            borderTopLeftRadius: 16,
            borderTopRightRadius: 16,
            pb: 2
          },
          // Increase zIndex to be higher than the TasksModal (2100)
          zIndex: 2200
        }}
      >
        <AppBar position="static" color="transparent" elevation={0}>
          <Toolbar sx={{ justifyContent: 'space-between', px: 2 }}>
            <Typography component="span" variant="h6">Notes for Task</Typography>
            <IconButton 
              edge="end" 
              onClick={onClose} 
              aria-label="close"
              size="small"
            >
              <CloseIcon />
            </IconButton>
          </Toolbar>
        </AppBar>
        
        <Box sx={{ px: 2, pb: 2 }}>
          <Typography variant="subtitle1" color="text.primary" fontWeight="medium" gutterBottom>
            {taskSummary}
          </Typography>
        </Box>
        
        <Box sx={{ 
          px: 2, 
          flex: 1, 
          display: 'flex', 
          flexDirection: 'column',
          overflow: 'hidden' 
        }}>
          <TextField
            multiline
            fullWidth
            value={editedNotes}
            onChange={(e) => setEditedNotes(e.target.value)}
            placeholder="Add your notes here..."
            variant="outlined"
            sx={{ 
              flex: 1,
              display: 'flex',
              minHeight: '200px',
              '& .MuiInputBase-root': {
                height: '100%',
                display: 'flex',
                flexDirection: 'column'
              },
              '& .MuiInputBase-inputMultiline': {
                flex: 1,
                height: 'auto !important'
              }
            }}
          />
        </Box>
        
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'flex-end', 
          px: 2, 
          pt: 2 
        }}>
          <Button onClick={onClose} sx={{ mr: 1 }}>Cancel</Button>
          <Button 
            variant="contained" 
            color="primary"
            onClick={handleSave}
          >
            Save Notes
          </Button>
        </Box>
      </Drawer>
    );
  }

  // Desktop version stays the same
  return (
    <Dialog
      open={isOpen}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      sx={{
        '& .MuiBackdrop-root': {
          backdropFilter: 'blur(2px)'
        },
        '& .MuiDialog-paper': {
          borderRadius: 2,
          boxShadow: theme => `0 8px 32px ${alpha(theme.palette.common.black, 0.2)}`,
          p: 1,
          height: {
            xs: 'auto',
            sm: '80vh'
          },
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden'
        }
      }}
    >
      <DialogTitle 
        sx={{ 
          display: 'flex', 
          justifyContent: 'space-between',
          alignItems: 'center',
          pb: 1,
          px: 3
        }}
      >
        <Typography component="span" variant="h6">Notes for Task</Typography>
        <IconButton 
          edge="end" 
          onClick={onClose} 
          aria-label="close"
          size="small"
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      
      <Box sx={{ px: 3, pb: 2 }}>
        <Typography variant="subtitle1" color="text.primary" fontWeight="medium" gutterBottom>
          {taskSummary}
        </Typography>
      </Box>
      <DialogContent 
        dividers
        sx={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          p: 3,
          overflow: 'auto'
        }}
      >
        <TextField
          autoFocus
          multiline
          fullWidth
          value={editedNotes}
          onChange={(e) => setEditedNotes(e.target.value)}
          placeholder="Add your notes here..."
          variant="outlined"
          sx={{ 
            flex: 1,
            display: 'flex',
            '& .MuiInputBase-root': {
              height: '100%',
              display: 'flex',
              flexDirection: 'column'
            },
            '& .MuiInputBase-inputMultiline': {
              flex: 1,
              height: 'auto !important'
            }
          }}
        />
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose}>Cancel</Button>
        <Button 
          variant="contained" 
          color="primary"
          onClick={handleSave}
        >
          Save Notes
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default NotesModal;